import { useCallback, useEffect, useState } from "react";
import { api } from "@shared/api";
import { Banner, Empty, Field, Loading, StatusPill } from "@shared/ui";
import {
  PROVIDER_LABELS,
  type AiProviderRow,
  type ProviderCatalogEntry,
  type Workspace,
} from "@shared/types";

export function ProvidersPage() {
  const [providers, setProviders] = useState<AiProviderRow[] | null>(null);
  const [catalog, setCatalog] = useState<ProviderCatalogEntry[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, cat, ws] = await Promise.all([
        api.get<{ providers: AiProviderRow[] }>("/api/admin/ai-providers"),
        api.get<{ providers: ProviderCatalogEntry[] }>("/api/admin/ai-providers/catalog"),
        api.get<{ workspaces: Workspace[] }>("/api/admin/workspaces"),
      ]);
      setProviders(list.providers);
      setCatalog(cat.providers);
      setWorkspaces(ws.workspaces);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load AI keys");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>AI keys</h1>
          <p className="page-sub">
            Add one key per provider, or several. A key with no client attached is available to
            every client; a key attached to one client is used only for them.
          </p>
        </div>
        <button className="primary" onClick={() => setAdding((v) => !v)}>
          {adding ? "Cancel" : "Add key"}
        </button>
      </div>

      {error ? <Banner kind="error">{error}</Banner> : null}

      {adding ? (
        <AddProviderCard
          catalog={catalog}
          workspaces={workspaces}
          onDone={() => {
            setAdding(false);
            void load();
          }}
        />
      ) : null}

      {providers === null ? (
        <Loading label="Loading keys" />
      ) : providers.length === 0 ? (
        <Empty title="No AI keys yet">
          Add a Claude key to write posts. Add Grok, OpenAI or Gemini for images and video.
        </Empty>
      ) : (
        providers.map((p) => (
          <ProviderCard key={p.id} provider={p} workspaces={workspaces} onChanged={load} />
        ))
      )}
    </>
  );
}

function ProviderCard({
  provider,
  workspaces,
  onChanged,
}: {
  provider: AiProviderRow;
  workspaces: Workspace[];
  onChanged: () => void;
}) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const owner = workspaces.find((w) => w.id === provider.workspaceId);

  async function test() {
    setTesting(true);
    setResult(null);
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(
        `/api/admin/ai-providers/${provider.id}/test`,
      );
      setResult(res.ok ? "Key works." : (res.error ?? "The key failed."));
      onChanged();
    } catch (err) {
      setResult(err instanceof Error ? err.message : "Test failed");
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    await api.delete(`/api/admin/ai-providers/${provider.id}`);
    onChanged();
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>{provider.label}</h2>
          <p className="muted" style={{ marginTop: 4 }}>
            {PROVIDER_LABELS[provider.provider] ?? provider.provider} ·{" "}
            <span className="mono">{provider.apiKeyHint}</span> ·{" "}
            {owner ? owner.name : "all clients"}
          </p>
        </div>
        <StatusPill status={provider.status} />
      </div>

      <p className="muted" style={{ marginTop: 10 }}>
        Can do: {provider.capabilities.join(", ")}
        {provider.textModel ? ` · text model ${provider.textModel}` : ""}
      </p>

      {provider.status === "error" && provider.lastError ? (
        <p className="mono" style={{ marginTop: 10, color: "var(--danger)" }}>
          {provider.lastError}
        </p>
      ) : null}

      {result ? (
        <p className="muted" style={{ marginTop: 10 }}>
          {result}
        </p>
      ) : null}

      <div className="actions" style={{ marginTop: 14 }}>
        <button className="small" onClick={() => void test()} disabled={testing}>
          {testing ? "Testing…" : "Test key"}
        </button>
        <button className="small danger" onClick={() => void remove()}>
          Delete
        </button>
      </div>
    </div>
  );
}

function AddProviderCard({
  catalog,
  workspaces,
  onDone,
}: {
  catalog: ProviderCatalogEntry[];
  workspaces: Workspace[];
  onDone: () => void;
}) {
  const [provider, setProvider] = useState("anthropic");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [textModel, setTextModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entry = catalog.find((c) => c.provider === provider);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/admin/ai-providers", {
        provider,
        label: label || `${PROVIDER_LABELS[provider] ?? provider} key`,
        apiKey,
        workspaceId: workspaceId || null,
        textModel: textModel || null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the key");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h2 style={{ marginBottom: 14 }}>Add an AI key</h2>
      {error ? <Banner kind="error">{error}</Banner> : null}

      <div className="row">
        <Field label="Provider">
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {catalog.map((c) => (
              <option key={c.provider} value={c.provider}>
                {PROVIDER_LABELS[c.provider] ?? c.provider}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Label" hint="How you will recognise this key.">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`${PROVIDER_LABELS[provider] ?? provider} key`}
          />
        </Field>
      </div>

      <Field label="API key" hint="Stored encrypted. It is never shown again after you save it.">
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          required
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <div className="row">
        <Field label="Used by" hint="Leave as all clients unless this key belongs to one client.">
          <select value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
            <option value="">All clients</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Text model"
          hint={entry ? `Leave blank to use ${entry.defaults.text}.` : undefined}
        >
          <input
            value={textModel}
            onChange={(e) => setTextModel(e.target.value)}
            placeholder={entry?.defaults.text ?? ""}
          />
        </Field>
      </div>

      {entry ? (
        <p className="muted" style={{ marginBottom: 14 }}>
          This provider can do: {entry.capabilities.join(", ")}.
        </p>
      ) : null}

      <div className="actions">
        <button type="submit" className="primary" disabled={busy || apiKey.trim().length < 8}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
    </form>
  );
}
